import { offers } from './product'
import type { CustomEventName } from '../utils/tracking'

/**
 * Shopify variants for the private negotiation offers.
 *
 * Paste each variant ID (the number in the product's cart link, e.g.
 * shop.gritvuegear.com/cart/45095500546141:1 → '45095500546141') between the
 * quotes. Any offer left empty is skipped automatically, so the flow never
 * sends a customer to a product that does not exist yet.
 */
export const UPSELL_VARIANTS = {
  /** GritVue X1 Complete System + Lifetime Replacement Warranty · $69.90 (normal +$19.95 upgrade) */
  SINGLE_X1_LIFETIME_6990_VARIANT_ID: '',
  /** GritVue X1 3-System Lifetime Upgrade · Private offer · $84.90 */
  THREE_SYSTEM_LIFETIME_PRIVATE_8490_VARIANT_ID: '',
  /** GritVue X1 3-System Lifetime Upgrade · Final offer · $79.90 */
  THREE_SYSTEM_LIFETIME_FINAL_7990_VARIANT_ID: '',
  /** GritVue X1 Complete System + Lifetime Replacement Warranty · Private offer · $59.90 */
  SINGLE_X1_LIFETIME_PRIVATE_5990_VARIANT_ID: '',
  /** GritVue X1 Complete System + Lifetime Replacement Warranty · Final private offer · $56.90 */
  SINGLE_X1_LIFETIME_FINAL_5690_VARIANT_ID: '',
}

export const SINGLE_PRICE = 49.95
export const LIFETIME_UPGRADE_PRICE = 19.95
export const SINGLE_WITH_LIFETIME_PRICE = 69.9
export const TRIO_PRICE = 99.9

const singleVariant = offers.find((o) => o.id === 'single')?.variantId ?? ''
const trioVariant = offers.find((o) => o.id === 'trio')?.variantId ?? ''

/** Where a button finally sends the customer. Always a single cart line, never stacked. */
export interface CartTarget {
  variantId: string
  quantity: number
  name: string
  value: number
}

export interface UpsellStep {
  id: string
  plate: string
  headline: string
  highlight: string
  intro: string
  current: { label: string; detail: string; total: number }
  upgrade: { label: string; detail: string; total: number }
  /** "Same price" / "Only $30 more" / "+$9.95" */
  extra: string
  includes: string[]
  /** Warranty price ladder, shown on the lifetime-only offers. */
  valueLine?: { normal: string; today: string; save: string }
  /** Spells out that the number shown is the new order total, not the extra charge. */
  clarification?: string
  lifetimeMedal?: boolean
  accept: string
  decline: string
  target: CartTarget
  events?: { viewed: CustomEventName; accepted: CustomEventName; declined: CustomEventName }
}

export interface UpsellFlow {
  kind: 'single-lifetime' | 'two-systems' | 'single-no-lifetime'
  steps: UpsellStep[]
  /** Where the customer goes after declining every step: exactly what they picked. */
  fallback: CartTarget
}

const trioIncludes = [
  '3 complete X1 systems',
  '9 interchangeable lenses: 3 Clear, 3 Amber, 3 Smoke',
  'Free U.S. shipping',
  'Free mystery gift',
  'Lifetime Replacement Coverage on all 3',
]

export const isLifetimeToggleAvailable = () => Boolean(UPSELL_VARIANTS.SINGLE_X1_LIFETIME_6990_VARIANT_ID)

const configured = (steps: UpsellStep[]) => steps.filter((step) => Boolean(step.target.variantId))

/** PATH 1 · 1 X1 + normal $19.95 lifetime ($69.90) → three-system negotiation. */
function singleLifetimeFlow(): UpsellFlow {
  const current = {
    label: '1 X1 + Lifetime',
    detail: '1 complete system with Lifetime Replacement Coverage',
    total: SINGLE_WITH_LIFETIME_PRICE,
  }
  const trioUpgrade = (total: number) => ({
    label: '3 X1 + Lifetime on all 3',
    detail: '3 complete systems, 9 lenses, lifetime coverage on every unit',
    total,
  })
  return {
    kind: 'single-lifetime',
    steps: configured([
      {
        id: 'trio-9990',
        plate: 'Before you check out',
        headline: "You're already at $69.90.",
        highlight: 'For only $30 more, take three.',
        intro: 'Two more complete systems, all three covered for life, free shipping and the mystery gift.',
        current,
        upgrade: trioUpgrade(99.9),
        extra: 'Only $30 more',
        includes: trioIncludes,
        lifetimeMedal: true,
        accept: 'Yes, give me 3 for $99.90',
        decline: 'No thanks, keep my 1 X1 + Lifetime',
        target: { variantId: trioVariant, quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: 99.9 },
      },
      {
        id: 'trio-8490',
        plate: 'Private offer',
        headline: "Let's meet in the middle.",
        highlight: "We'll cover half.",
        intro: 'Same three complete systems with lifetime coverage on all three. Half the difference, gone.',
        current,
        upgrade: trioUpgrade(84.9),
        extra: 'Only $15 more',
        includes: trioIncludes,
        lifetimeMedal: true,
        accept: 'Yes, 3 for $84.90',
        decline: 'No thanks, keep my 1 X1 + Lifetime',
        target: {
          variantId: UPSELL_VARIANTS.THREE_SYSTEM_LIFETIME_PRIVATE_8490_VARIANT_ID,
          quantity: 1,
          name: 'GritVue X1 3-System Lifetime Upgrade · Private offer',
          value: 84.9,
        },
      },
      {
        id: 'trio-7990',
        plate: 'Final offer',
        headline: 'One last deal.',
        highlight: 'Only $10 more.',
        intro: 'Three complete systems, lifetime coverage on all three, for $10 over what you have now. This is as low as it goes.',
        current,
        upgrade: trioUpgrade(79.9),
        extra: 'Only $10 more',
        includes: trioIncludes,
        lifetimeMedal: true,
        accept: 'Yes, 3 for $79.90',
        decline: 'No thanks, keep my 1 X1 + Lifetime',
        target: {
          variantId: UPSELL_VARIANTS.THREE_SYSTEM_LIFETIME_FINAL_7990_VARIANT_ID,
          quantity: 1,
          name: 'GritVue X1 3-System Lifetime Upgrade · Final offer',
          value: 79.9,
        },
      },
    ]),
    fallback: {
      variantId: UPSELL_VARIANTS.SINGLE_X1_LIFETIME_6990_VARIANT_ID,
      quantity: 1,
      name: 'GritVue X1 Complete System + Lifetime Replacement Warranty',
      value: SINGLE_WITH_LIFETIME_PRICE,
    },
  }
}

/** PATH 2 · exactly 2 singles ($99.90) → free third system. */
function twoSystemsFlow(): UpsellFlow {
  return {
    kind: 'two-systems',
    steps: configured([
      {
        id: 'free-third',
        plate: 'You just unlocked a free X1',
        headline: 'Congratulations.',
        highlight: 'Your 3rd X1 is free.',
        intro: 'Why take 2 when you can get 3 for the same $99.90?',
        current: { label: '2 X1 systems', detail: '6 lenses · 1-year coverage', total: TRIO_PRICE },
        upgrade: { label: '3 X1 systems', detail: '9 lenses · lifetime on all 3', total: TRIO_PRICE },
        extra: 'Same price',
        includes: [
          '3 complete X1 systems',
          '9 interchangeable lenses instead of 6: 3 Clear, 3 Amber, 3 Smoke',
          'Free U.S. shipping',
          'Free mystery gift',
          'Lifetime Replacement Coverage on all 3 X1 systems',
        ],
        clarification: 'Your 3rd X1 is completely free. You pay the same $99.90.',
        lifetimeMedal: true,
        accept: 'Claim my free X1',
        decline: 'No thanks, I only want 2 for the same $99.90',
        target: { variantId: trioVariant, quantity: 1, name: 'Buy 2, Get 1 Free · 3 complete systems', value: 99.9 },
        events: {
          viewed: 'two_x1_free_third_viewed',
          accepted: 'two_x1_free_third_accepted',
          declined: 'two_x1_free_third_declined',
        },
      },
    ]),
    fallback: { variantId: singleVariant, quantity: 2, name: '1 GritVue X1 Complete System', value: 99.9 },
  }
}

/** PATH 3 · 1 X1 without lifetime ($49.95) → $9.95, then $6.95 lifetime upgrade. */
function singleNoLifetimeFlow(): UpsellFlow {
  const current = { label: '1 X1 system', detail: 'Includes 1-year replacement coverage', total: SINGLE_PRICE }
  return {
    kind: 'single-no-lifetime',
    steps: configured([
      {
        id: 'lifetime-995',
        plate: 'Private upgrade',
        headline: 'One last thing before you go.',
        highlight: 'Lifetime coverage for half the normal price.',
        intro:
          'Protect your X1 beyond the included one-year coverage. Upgrade to Lifetime Replacement Coverage before checkout for only $9.95.',
        current,
        upgrade: { label: '1 X1 + Lifetime', detail: 'Break it. We replace it.', total: 59.9 },
        extra: '+$9.95',
        includes: ['Everything in your current order', 'Lifetime Replacement Warranty. Break it. We replace it.'],
        valueLine: { normal: '$19.95', today: '$9.95', save: 'Save $10' },
        clarification: 'You are not paying $59.90 more. Your new order total is $59.90.',
        lifetimeMedal: true,
        accept: 'Yes, protect my X1 for life',
        decline: 'No thanks, keep my one-year coverage',
        target: {
          variantId: UPSELL_VARIANTS.SINGLE_X1_LIFETIME_PRIVATE_5990_VARIANT_ID,
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty · Private offer',
          value: 59.9,
        },
        events: {
          viewed: 'single_lifetime_995_viewed',
          accepted: 'single_lifetime_995_accepted',
          declined: 'single_lifetime_995_declined',
        },
      },
      {
        id: 'lifetime-695',
        plate: 'Final private upgrade',
        headline: 'OK. One final offer.',
        highlight: 'Lifetime coverage for $6.95.',
        intro:
          "We understand. But we'd still rather see your X1 protected for life, so we'll make it even easier. Instead of $19.95 or $9.95, pay only $6.95.",
        current,
        upgrade: { label: '1 X1 + Lifetime', detail: '$49.95 + $6.95', total: 56.9 },
        extra: '+$6.95',
        includes: ['Everything in your current order', 'Lifetime Replacement Warranty. Break it. We replace it.'],
        valueLine: { normal: '$19.95', today: '$6.95', save: 'Save $13' },
        clarification: 'You are not paying $56.90 more. Your final order total is $56.90.',
        lifetimeMedal: true,
        accept: 'Yes, protect my X1 for $6.95',
        decline: 'No thanks, keep my one-year coverage',
        target: {
          variantId: UPSELL_VARIANTS.SINGLE_X1_LIFETIME_FINAL_5690_VARIANT_ID,
          quantity: 1,
          name: 'GritVue X1 Complete System + Lifetime Replacement Warranty · Final private offer',
          value: 56.9,
        },
        events: {
          viewed: 'single_lifetime_695_viewed',
          accepted: 'single_lifetime_695_accepted',
          declined: 'single_lifetime_695_declined',
        },
      },
    ]),
    fallback: { variantId: singleVariant, quantity: 1, name: '1 GritVue X1 Complete System', value: SINGLE_PRICE },
  }
}

/**
 * Picks exactly one flow for a single-system checkout, so sequences never overlap.
 * Returns null when there is nothing to negotiate (3+ singles, or no step configured).
 */
export function flowForSingle(quantity: number, lifetime: boolean): UpsellFlow | null {
  let flow: UpsellFlow | null = null
  if (quantity === 2) flow = twoSystemsFlow()
  else if (quantity === 1 && lifetime && isLifetimeToggleAvailable()) flow = singleLifetimeFlow()
  else if (quantity === 1 && !lifetime) flow = singleNoLifetimeFlow()
  return flow && flow.steps.length > 0 ? flow : null
}
